// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { CodeableConcept, Coding, Encounter, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type { JSX } from 'react';
import { useEffect } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router';
import { EncounterChart } from '../../components/encounter/EncounterChart';
import { showErrorNotification } from '../../utils/notifications';

export const EncounterChartPage = (): JSX.Element | null => {
  const { encounterId } = useParams();
  const medplum = useMedplum();
  const navigate = useNavigate();

  useEffect(() => {
    if (!encounterId) {
      return undefined;
    }

    let active = true;
    medplum
      .readResource('Encounter', encounterId)
      .then((encounter: Encounter) => {
        const patientId = encounter.subject?.reference?.replace(/^Patient\//, '');
        const isCheckIn = encounter.type?.some((type: CodeableConcept) =>
          type.coding?.some(
            (coding: Coding) =>
              coding.code?.toLowerCase() === 'check-in' || coding.display?.toLowerCase() === 'check-in'
          )
        );
        if (active && isCheckIn && patientId) {
          navigate(`/Patient/${patientId}/Encounter/${encounterId}/coordinator-note`, { replace: true });
        }
      })
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, [encounterId, medplum, navigate]);

  if (!encounterId) {
    showErrorNotification('Encounter ID not found');
    return null;
  }

  const encounterRef: Reference<Encounter> = {
    reference: `Encounter/${encounterId}`,
  };

  return (
    <>
      <EncounterChart encounter={encounterRef} />
      <Outlet />
    </>
  );
};
